/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { OrderDetailsTableStyle } from "@eisinc/App/SharedStyles/OrderDetailsStyles";
import { screen } from "@eisinc/XDL/Helpers";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import translate from "@insite/client-framework/Translate";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    orderLineShipments: any;
    unitOfMeasure?: string;
}

export interface OrderDetailsShipmentDetailsProps {
    tableRowStyle?: DataTableRowProps;
    tableCellStyle?: DataTableCellProps;
    hiddenHeaderStyle?: HiddenProps;
    tableCellGridStyle?: GridItemProps;
    shippingDetailsTableHeaderStyle?: DataTableHeadProps;
    tableHeaderStyle?: DataTableHeaderProps;
}

export const orderDetailsShipmentDetailsStyles: OrderDetailsShipmentDetailsProps = {
    tableRowStyle: {
        css: css`
            &&&:hover,
            &&&:focus {
                background: transparent;
                cursor: unset;
            }
            @media print {
                &&& {
                    border: 0;
                    display: flex;
                    flex-direction: column;
                }
            }
            ${screen("lte", "md")`
                display: flex;
                flex-direction: column;
                &:first-child {
                    padding: 20px 0;
                    border-top-width: 1px;
                    border-top-style: solid;
                }
                td {
                    height: 100%;
                    padding: 6px 0;
                }
            `}
        `,
    },
    tableCellStyle: {
        ...OrderDetailsTableStyle.tableCellStyle,
        css: css`
            height: 40px;
            border-bottom: 0;
        `,
    },
    hiddenHeaderStyle: {
        ...OrderDetailsTableStyle.hiddenHeaderStyle,
    },
    tableCellGridStyle: {
        ...OrderDetailsTableStyle.tableCellGridStyle,
    },
    shippingDetailsTableHeaderStyle: {
        ...OrderDetailsTableStyle.shippingDetailsTableHeaderStyle,
    },
    tableHeaderStyle: {
        ...OrderDetailsTableStyle.tableHeaderStyle,
    },
};

type Props = OwnProps;

const styles = orderDetailsShipmentDetailsStyles;

const OrderDetailsShipmentDetails: FC<Props> = ({ orderLineShipments, unitOfMeasure }) => {
    const headerColumns = [
        `${translate("Shipment Date")}`,
        `${translate("Qty. Shipped")}`,
        `${translate("UOM")}`,
        `${translate("Tracking #s")}`,
    ];

    return (
        <DataTable
            css={css`
                width: 100%;
            `}
        >
            <DataTableHead {...styles.shippingDetailsTableHeaderStyle}>
                {headerColumns.map((column, index) => (
                    <DataTableHeader key={`${column}_${index}`} {...styles.tableHeaderStyle}>
                        {column}
                    </DataTableHeader>
                ))}
            </DataTableHead>
            <DataTableBody>
                {orderLineShipments.map((orderLineShipment: any) => {
                    return (
                        <DataTableRow key={orderLineShipment.id} {...styles.tableRowStyle}>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[0]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>
                                    <LocalizedDateTime
                                        dateTime={new Date(orderLineShipment.shipmentDate)}
                                        options={{ year: "numeric", month: "numeric", day: "numeric", timeZone: "UTC" }}
                                    />
                                </GridItem>
                            </DataTableCell>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[1]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>
                                    {orderLineShipment.qtyShipped || "– –"}
                                </GridItem>
                            </DataTableCell>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[2]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>{unitOfMeasure || "– –"}</GridItem>
                            </DataTableCell>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[3]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>
                                    {orderLineShipment.trackingUrl ? (
                                        <Link href={orderLineShipment.trackingUrl} target="_blank">
                                            {orderLineShipment.trackingNumber}
                                        </Link>
                                    ) : (
                                        <Typography>{orderLineShipment.trackingNumber || "– –"}</Typography>
                                    )}
                                </GridItem>
                            </DataTableCell>
                        </DataTableRow>
                    );
                })}
            </DataTableBody>
        </DataTable>
    );
};

export default OrderDetailsShipmentDetails;
